import { List, Map } from 'immutable';

import { findAnalogue, findAnalogueScale, getAvailable } from '../taxonomy';
import { setAllInfluenced, updateInfluence } from './influence';
import { isValidNumber, toFixed, toString } from '../number';
import { getDeletedCombinations } from './combinations';
import { newVariations } from '../new/product';
import { getSize } from '../iterable/getSize';
import { reduce } from '../iterable/reduce';
import { get } from '../iterable/get';

import { COMBINATIONS, DELETED, INFLUENCE, VARIANTS } from '../../constants/variations';
import { SHOPIFY_CATEGORY_ENABLED } from '../../constants/featureFlags';
import { CUSTOM_PROPERTY_IDS } from '../../constants/taxonomy';
import { AMOUNT, VALUE } from '../../constants/product';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY } from '../../constants';
import ATTRIBUTE from '../../constants/attributes';

function getOptionImageHash({ option, imagesById, photos }) {
  const [imageHash, channelImageId, velaImageId] = get('imageHash', 'channelImageId', 'velaImageId')(option);
  let result;

  function findImage(image) {
    return get('channel_image_id')(image) === channelImageId;
  }

  function findPhoto(value, key) {
    return function findItem(item) {
      return item.get(key) === value;
    };
  }

  if (imageHash) {
    result = imageHash;
  } else if (imagesById) {
    if (velaImageId) {
      result = get([velaImageId, 'hash'])(imagesById);
    } else if (channelImageId) {
      const sameImage = find(imagesById, findImage);

      if (sameImage) {
        result = get('hash')(sameImage);
      }
    }
  } else if (photos && (velaImageId || channelImageId)) {
    const value = velaImageId || channelImageId;
    const key = velaImageId ? 'id' : 'channel_image_id';
    const photo = photos.get(VALUE).find(findPhoto(value, key));

    if (photo) {
      const hash = photo.get('hash');
      result = hash || null;
    }
  }

  return result || null;
}

function getCombinationImageHashAndImageId({ imagesById, photos, variant }) {
  const [
    velaImageId,
    imageHash,
    channelImageId,
  ] = get('vela_image_id', 'shopify_image_hash', 'shopify_image_id')(variant);

  const result = {
    imageId: null,
    imageHash: null,
  };

  function findPhoto(photo) {
    return photo.get('id') === velaImageId;
  }

  if (imageHash) {
    result.imageHash = imageHash;
  } else if (channelImageId) {
    result.imageId = channelImageId;
  } else if (velaImageId) {
    if (imagesById) {
      const [channelImageId, hash] = get([velaImageId, 'channel_image_id'], [velaImageId, 'hash'])(imagesById);

      if (channelImageId) {
        result.imageId = channelImageId;
      } else if (hash) {
        result.imageHash = hash;
      }
    } else if (photos) {
      const photo = photos.get(VALUE).find(findPhoto);

      if (photo) {
        const channelImageId = photo.get('channel_image_id');
        const hash = photo.get('hash');

        if (channelImageId) {
          result.imageId = channelImageId;
        } else if (hash) {
          result.imageHash = hash;
        }
      }
    }
  }

  return result;
}

function fixCustomProperty({ channel, property, variationIndex }) {
  return property === CUSTOM_PROPERTY_IDS[channel][variationIndex]
    ? property
    : CUSTOM_PROPERTY_IDS[channel][variationIndex];
}

export function shapeVariationsForApp({ category, channel, data, imagesById, inventory, photos }) {
  let variations = newVariations({ channel });
  const unset = inventory ? '' : null;

  function reduceEtsyVariations(result, variation) {
    const name = get('formattedName')(variation);

    if (!name) return result;

    function mapOptions(option) {
      const [id, value, valueId] = get('id', 'value', 'valueId')(option);
      return Map({
        id,
        value,
        valueId,
        imageHash: getOptionImageHash({ option, imagesById, photos }),
      });
    }

    let influence = result.get(INFLUENCE);

    const [
      first,
      id,
      influencesPrice = false,
      influencesQuantity = false,
      influencesSku = false,
      options,
      propertyId,
      scalingOptionId,
    ] = get(
      'first',
      'id',
      'influencesPrice',
      'influencesQuantity',
      'influencesSku',
      'options',
      'propertyId',
      'scalingOptionId',
    )(variation);

    const variationIndex = first ? 0 : 1;

    if (influencesPrice) {
      influence = influence.setIn([ATTRIBUTE.PRICE, variationIndex], true);
    }

    if (influencesQuantity) {
      influence = influence.setIn([ATTRIBUTE.QUANTITY, variationIndex], true);
    }

    if (influencesSku) {
      influence = influence.setIn([ATTRIBUTE.SKU, variationIndex], true);
    }

    const scale = isValidNumber(scalingOptionId) ? parseInt(scalingOptionId) : undefined;
    const property = isValidNumber(propertyId) ? parseInt(propertyId) : undefined;
    let variant = Map({
      id,
      name,
      options: List(options.map(mapOptions)),
      property,
    });

    if (category && category !== AMOUNT.ZERO) {
      if (!CUSTOM_PROPERTY_IDS[channel] || !CUSTOM_PROPERTY_IDS[channel].includes(property)) {
        const { availableScales, availableVariations } = getAvailable({ category, channel, property, scale });
        const sameVariation = availableVariations.find(findAnalogue({ id: property }));

        if (sameVariation) {
          if (sameVariation.name !== name) {
            variant = variant.set('name', sameVariation.name);
          }
        } else {
          const analogueVariation = availableVariations.find(findAnalogue({ name }));

          if (analogueVariation && analogueVariation.id !== property) {
            variant = variant.set('property', analogueVariation.id);
          }
        }

        if (getSize(availableScales)) {
          if (!scale) {
            variant = variant.set('scale', 0);
          } else {
            const analogueScale = availableScales.find(findAnalogueScale({ channel, scale }));

            if (analogueScale && analogueScale.id !== scale) {
              variant = variant.set('scale', analogueScale.id);
            } else {
              variant = variant.set('scale', scale);
            }
          }
        } else if (scale) {
          variant = variant.set('scale', scale);
        }
      } else {
        variant = variant.set('property', fixCustomProperty({ channel, property, variationIndex }));
      }
    } else if (CUSTOM_PROPERTY_IDS[channel] && CUSTOM_PROPERTY_IDS[channel].includes(property)) {
      variant = variant.set('property', fixCustomProperty({ channel, property, variationIndex }));
    }

    return result
      .set(INFLUENCE, influence)
      .set(VARIANTS, first ? result.get(VARIANTS).unshift(variant) : result.get(VARIANTS).push(variant));
  }

  function reduceEtsyOfferings(result, offering) {
    const [
      id,
      price,
      quantity,
      sku,
      visibility,
      variationOptions,
    ] = get('id', 'price', 'quantity', 'sku', 'visibility', 'variationOptions')(offering);

    if (!getSize(variationOptions)) return result;

    function mapOptions(option) {
      const [optionId, variantId] = get('optionId', 'variationId')(option);

      function findVariantById(variant) {
        return variantId === variant.get('id');
      }

      function findOptionById(variantOption) {
        return optionId === variantOption.get('id');
      }

      const sameVariant = result.get(VARIANTS).find(findVariantById);
      const options = sameVariant.get('options');
      const sameOption = options.find(findOptionById);
      const name = sameOption.get('value');

      return Map({ name, optionId, variantId });
    }

    const combination = Map({
      id,
      options: List(variationOptions.map(mapOptions)),
      [ATTRIBUTE.PRICE]: toFixed(price, 2) || unset,
      [ATTRIBUTE.QUANTITY]: toString(quantity) || unset,
      [ATTRIBUTE.SKU]: sku || unset,
      [ATTRIBUTE.VISIBILITY]: visibility,
    });

    return result.set(COMBINATIONS, result.get(COMBINATIONS).push(combination));
  }

  function reduceShopifyOptions(result, option, optionIndex) {
    const id = get('id')(option);
    const name = get('shopify_name')(option);
    const values = get('shopify_values')(option);
    const attribute = get('shopify_taxonomy_attribute_id')(option);

    if (!name) return result;

    function mapValues(value) {
      return Map({ value: value.trim() });
    }

    const variant = {
      id: id || -1 - optionIndex,
      name,
      options: List(values.map(mapValues)),
    };

    if (SHOPIFY_CATEGORY_ENABLED) {
      variant.attribute = attribute || null;
    }

    return result.set(VARIANTS, result.get(VARIANTS).push(Map(variant)));
  }

  function reduceShopifyVariants(result, variant, variantIndex) {
    function reduceOptions(options, name) {
      return name
        ? options.push(Map({ name: name.trim() }))
        : options;
    }

    const [
      id,
      sku,
      cpi,
      price,
      weight,
      option1,
      option2,
      option3,
      barcode,
      unit,
      chargeTax,
      cap,
      physical,
      quantity,
      hsCode,
      inventoryPolicy,
      countryCode,
      inventoryManagement,
    ] = get(
      'id',
      'shopify_sku',
      'shopify_cost',
      'shopify_price',
      'shopify_weight',
      'shopify_option1',
      'shopify_option2',
      'shopify_option3',
      'shopify_barcode',
      'shopify_weight_unit',
      'shopify_taxable',
      'shopify_compare_at_price',
      'shopify_requires_shipping',
      'shopify_inventory_quantity',
      'shopify_harmonized_system_code',
      'shopify_inventory_policy',
      'shopify_country_code_of_origin',
      'shopify_inventory_management',
    )(variant);

    const combination = Map({
      [ATTRIBUTE.BARCODE]: barcode || unset,
      [ATTRIBUTE.CAP]: toFixed(cap, 2) || unset,
      [ATTRIBUTE.CHARGE_TAX]: chargeTax,
      [ATTRIBUTE.CONTINUE_SELLING]: inventoryPolicy === 'continue',
      [ATTRIBUTE.COUNTRY_CODE]: countryCode || unset,
      [ATTRIBUTE.CPI]: toFixed(cpi, 2) || unset,
      [ATTRIBUTE.HS_CODE]: hsCode || unset,
      [ATTRIBUTE.PHYSICAL]: physical,
      [ATTRIBUTE.PRICE]: toFixed(price, 2) || unset,
      [ATTRIBUTE.QUANTITY]: quantity || unset,
      [ATTRIBUTE.SKU]: sku || unset,
      [ATTRIBUTE.TRACK_QUANTITY]: inventoryManagement === SHOPIFY,
      [ATTRIBUTE.WEIGHT]: weight || unset,
      [ATTRIBUTE.UNIT]: unit || unset,
      id: id || -1 - variantIndex,
      options: [option1, option2, option3].reduce(reduceOptions, EMPTY.LIST),
      ...getCombinationImageHashAndImageId({ imagesById, photos, variant }),
    });

    return result.set(COMBINATIONS, result.get(COMBINATIONS).push(combination));
  }

  switch (channel) {
    case ETSY: {
      variations = reduce(get('variations')(data), variations, reduceEtsyVariations);
      variations = reduce(get('offerings')(data), variations, reduceEtsyOfferings);
      return setAllInfluenced(variations);
    }

    case SHOPIFY: {
      variations = reduce(get('options')(data), variations, reduceShopifyOptions);
      variations = reduce(get('variants')(data), variations, reduceShopifyVariants);
      variations = updateInfluence({ channel, variations });
      return variations.set(DELETED, getDeletedCombinations({ variations }));
    }

    default: {
      return variations;
    }
  }
}
